import { HTML_LANG, type Locale } from '@nexa/content';

/**
 * `<html lang>` của HTML mà server ĐÃ phục vụ, chụp ở **module scope** — tức trước khi React
 * render và trước khi react-helmet kịp ghi đè thuộc tính này bằng giá trị của cây React.
 *
 * Thời điểm chụp là toàn bộ giá trị của phép kiểm. Chụp trong `useEffect` là so cây React với
 * chính nó (helmet đã ghi đè xong), và phép so đó LUÔN đúng — kể cả khi nginx phục vụ sai file.
 */
const SERVED_HTML_LANG = typeof document === 'undefined' ? '' : document.documentElement.lang;

/**
 * Lưới cuối cho ca "nginx phục vụ file của ngôn ngữ này cho URL của ngôn ngữ kia".
 *
 * Vì sao cần dù đã có `localeFromSearch`: client suy locale từ `?lang`, nginx chọn file cũng từ
 * `?lang`, nhưng hai bên là HAI cài đặt của cùng một luật — `map $arg_lang $lang_dir` +
 * `try_files` bên nginx, và `distPathFor()` bên build. Chúng lệch được: đổi tên `__lang`, sửa
 * regex map, hoặc build hụt một cây. Mọi phép đo TĨNH vẫn xanh trong cả ba ca đó.
 *
 * Chỉ chạy ở bản build (`PROD`): ở `pnpm dev` không có prerender nên `<html lang>` là giá trị
 * mặc định của `index.html`, so sẽ ra dương tính giả. Ở `vite preview` thì kêu là ĐÚNG —
 * preview không có nginx nên `?lang=` thật sự không phục vụ đúng file (design §7).
 *
 * `console.error` chứ không ném: ném ở đây là trắng trang production để đổi lấy một dòng log.
 */
export function assertServedLocale(locale: Locale): void {
  if (!import.meta.env.PROD) return;
  if (SERVED_HTML_LANG === '') return;

  const expected = HTML_LANG[locale];
  if (SERVED_HTML_LANG === expected) return;

  console.error(
    `[i18n] Server phục vụ bản "${SERVED_HTML_LANG}" cho URL này, nhưng cây React đang dựng ` +
      `locale "${locale}" (chờ "${expected}"). Cây file trong dist và luật map ?lang của ` +
      'site.conf đã lệch nhau — xem docs/design/2026-08-27-lang-query-param-design.md §3.2.',
  );
}
